import React, { useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  Copy,
  Code2,
  Loader2,
  AlertCircle,
  ExternalLink,
  CreditCard,
  ArrowRightLeft,
  Wallet,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FlowStep, StepType } from '../types';
import { STEP_ICONS, STEP_TYPE_LABELS } from '../constants/stepStyles';
import { trpc } from '@/lib/api/trpc';
import { useIntegrationsList } from '@/lib/api/integrations';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import toast from 'react-hot-toast';
import { CollapsibleSection, SectionGroupHeader } from './CollapsibleSection';
import { WOO_CHECKOUT_PLUGINS_DOCS } from '@/components/integrations/forms/woocommerce-checkout-plugins';

interface WooCommerceEditorPanelProps {
  step: FlowStep;
  storeId: string;
  funnelId: string;
  templatesWithIntegrationStatus?: any[];
  onClose: () => void;
  onChange: (step: FlowStep) => void;
  onPreview?: () => void;
}

const INTEGRATION_LOGO = '/integrations/woo-icon.svg';

export function WooCommerceEditorPanel({
  step,
  storeId,
  funnelId,
  templatesWithIntegrationStatus,
  onClose,
  onChange,
  onPreview,
}: WooCommerceEditorPanelProps) {
  const [debug, setDebug] = useState(false);
  const [snippetContent, setSnippetContent] = useState('');
  const [isLoadingSnippet, setIsLoadingSnippet] = useState(false);
  const [redirectOnCart, setRedirectOnCart] = useState(true);
  const [redirectOnProduct, setRedirectOnProduct] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const generateScript = trpc.woocommerceScript.generateScript.useMutation();
  const { storeRoutes, navigate } = useStoreNavigate();

  const { data: integrations } = useIntegrationsList(storeId);
  const wooIntegration = integrations?.find((i: any) => i.provider === 'woocommerce');
  const wooStoreUrl = wooIntegration?.credentials?.url as string | undefined;

  const shopifyAvailable = templatesWithIntegrationStatus?.some(
    (t: any) => t.id === 'shopify-default' && t.integrationStatus?.connected,
  ) ?? false;

  const wooAvailable = templatesWithIntegrationStatus?.some(
    (t: any) => t.id === 'woocommerce-default' && t.integrationStatus?.connected,
  ) ?? false;

  const isConfigured = Boolean(step.metadata?.woocommerceConfigured) || wooAvailable || Boolean(wooIntegration);

  const handleDownloadPlugin = async () => {
    try {
      const res = await generateScript.mutateAsync({
        storeId,
        funnelId,
        stepId: step.id,
        format: 'plugin',
        config: { debug, redirectOnCart, redirectOnProduct },
      });

      if (!res) throw new Error('Failed to generate plugin');

      const byteCharacters = window.atob(res.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: res.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (!isConfigured) {
        onChange({ ...step, metadata: { ...step.metadata, woocommerceConfigured: true } });
      }
      toast.success('Plugin downloaded');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate plugin');
    }
  };

  const handleCopySnippet = async () => {
    if (!snippetContent) {
      setIsLoadingSnippet(true);
      try {
        const res = await generateScript.mutateAsync({
          storeId,
          funnelId,
          stepId: step.id,
          format: 'php',
          config: { debug, redirectOnCart, redirectOnProduct },
        });
        if (res?.content) {
          setSnippetContent(res.content);
          await navigator.clipboard.writeText(res.content);
          setIsCopied(true);
          toast.success('PHP snippet copied to clipboard');
          setTimeout(() => setIsCopied(false), 2000);
          if (!isConfigured) {
            onChange({ ...step, metadata: { ...step.metadata, woocommerceConfigured: true } });
          }
        }
      } catch (err: any) {
        toast.error(err?.message || 'Failed to generate snippet');
      } finally {
        setIsLoadingSnippet(false);
      }
      return;
    }
    await navigator.clipboard.writeText(snippetContent);
    setIsCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleDownloadGateway = async () => {
    try {
      const res = await generateScript.mutateAsync({
        storeId,
        funnelId,
        stepId: step.id,
        format: 'gateway',
        config: { debug },
      });

      if (!res) throw new Error('Failed to generate gateway plugin');

      const byteCharacters = window.atob(res.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: res.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Gateway plugin downloaded');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate gateway plugin');
    }
  };

  const renderFeatureToggle = (
    label: string,
    description: string,
    checked: boolean,
    onCheckedChange: (v: boolean) => void,
  ) => (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex-1">
        <Label className="text-sm font-medium">{label}</Label>
        <p className="text-xs text-slate-500 dark:text-slate-400">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      {/* ─── Header (matches StepEditorPanel) ──────────────────────── */}
      <div className="sticky top-0 z-40 border-b bg-white dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center gap-1.5 px-3 py-2">
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
            title="Close"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
          <Input
            value={step.label}
            onChange={(e) => onChange({ ...step, label: e.target.value })}
            placeholder="Page name..."
            className="h-7 min-w-0 flex-1 border-transparent bg-transparent px-1 text-sm font-semibold shadow-none focus-visible:border-slate-200 focus-visible:bg-white dark:focus-visible:border-slate-700 dark:focus-visible:bg-slate-900"
          />
          <Select
            value={step.type}
            onValueChange={(value: StepType) => onChange({ ...step, type: value })}
          >
            <SelectTrigger className="h-7 w-auto shrink-0 gap-1 border-none bg-slate-50 px-2 text-[11px] font-medium text-slate-500 shadow-none hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 [&_svg]:size-3">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Page Types</SelectLabel>
                {(Object.entries(STEP_TYPE_LABELS) as [StepType, string][]).map(([type, label]) => {
                  const TypeIcon = STEP_ICONS[type];
                  const isDisabled =
                    (type.includes('shopify') && !shopifyAvailable) ||
                    (type.includes('woocommerce') && !wooAvailable);
                  return (
                    <SelectItem key={type} value={type} disabled={isDisabled}>
                      <div className="flex items-center gap-2">
                        <TypeIcon className="h-3.5 w-3.5" />
                        <span className={isDisabled ? 'opacity-50' : ''}>{label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ─── Scrollable Content ──────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">

        {/* ─── Connection Status (always visible, top section) ──── */}
        <div className="border-b border-slate-200 p-4 dark:border-slate-700">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              WooCommerce Integration
            </h3>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/50">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                isConfigured ? 'bg-emerald-500/10' : 'bg-amber-500/10'
              }`}>
                {isConfigured ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {isConfigured ? 'Connected' : 'Setup required'}
                  </span>
                  {isConfigured && (
                    <span className="inline-flex h-5 items-center rounded-full bg-emerald-500/10 px-2 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">
                      Live
                    </span>
                  )}
                </div>
                <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                  {isConfigured && wooStoreUrl
                    ? wooStoreUrl.replace(/^https?:\/\//, '')
                    : 'Download and install the plugin on your WordPress site'}
                </p>
              </div>
            </div>
          </div>

          {/* Preview link */}
          {wooStoreUrl && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full gap-1.5 text-xs"
              onClick={() => window.open(wooStoreUrl.startsWith('http') ? wooStoreUrl : `https://${wooStoreUrl}`, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Preview in WooCommerce
            </Button>
          )}
        </div>

        {/* ─── Settings ──────────────────────────────────────────── */}
        <SectionGroupHeader title="Configuration" />

        {/* Full Redirect Plugin */}
        <CollapsibleSection
          title="Full Checkout Redirect"
          icon={ArrowRightLeft}
          badge={isConfigured ? 'Done' : undefined}
          badgeVariant="success"
          defaultOpen={!isConfigured}
        >
          <div className="space-y-4">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Replaces the entire WooCommerce checkout page. Customers are redirected to your Tagada checkout before they see the WooCommerce form.
            </p>
            <div className="flex gap-2">
              <Button
                className="flex-1 gap-2"
                onClick={handleDownloadPlugin}
                disabled={generateScript.isPending}
              >
                {generateScript.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download Redirect Plugin
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={handleCopySnippet}
                disabled={isLoadingSnippet}
              >
                {isLoadingSnippet ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : isCopied ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                {isCopied ? 'Copied!' : 'PHP'}
              </Button>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400">
              <p className="mb-2 font-medium text-slate-700 dark:text-slate-300">How it works</p>
              <ol className="list-decimal space-y-1 pl-4">
                <li>Customer adds products to cart on your WooCommerce store</li>
                <li>When they go to checkout, they are <strong>immediately redirected</strong> to Tagada</li>
                <li>Payment is completed on the Tagada checkout page</li>
                <li>Customer is sent back to your store on success</li>
              </ol>
            </div>

            <div className="space-y-3 border-t border-slate-200 pt-4 dark:border-slate-700">
              <p className="text-xs font-medium text-slate-700 dark:text-slate-300">Redirect Settings</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Re-download the plugin after changing these.
              </p>
              {renderFeatureToggle(
                'Redirect on Checkout',
                'Intercept WooCommerce checkout and redirect to TagadaPay',
                redirectOnCart,
                setRedirectOnCart,
              )}
              {renderFeatureToggle(
                'Redirect on Product Page',
                'Redirect directly from product "Buy Now" buttons',
                redirectOnProduct,
                setRedirectOnProduct,
              )}
            </div>
          </div>
        </CollapsibleSection>

        {/* Payment Gateway Plugin */}
        <CollapsibleSection
          title="Payment Gateway"
          icon={CreditCard}
          defaultOpen={false}
        >
          <div className="space-y-4">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Adds "Tagada Pay" as a payment method in the WooCommerce checkout.
              Customers fill out billing/shipping in WooCommerce, then are redirected to Tagada only for payment.
            </p>
            <Button
              className="w-full gap-2"
              variant="outline"
              onClick={handleDownloadGateway}
              disabled={generateScript.isPending}
            >
              {generateScript.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Download Gateway Plugin
            </Button>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400">
              <p className="mb-2 font-medium text-slate-700 dark:text-slate-300">How it works</p>
              <ol className="list-decimal space-y-1 pl-4">
                <li>Customer fills out the <strong>native WooCommerce checkout</strong></li>
                <li>They select "Tagada Pay" as their payment method</li>
                <li>On "Place Order", they are redirected to Tagada for payment</li>
                <li>After payment, they return to the WooCommerce thank-you page</li>
              </ol>
              <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-2 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
                <strong>Note:</strong> Do not use both plugins at the same time. The redirect plugin will take priority and bypass the gateway.
              </div>
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title="Inline card"
          icon={Wallet}
          defaultOpen={false}
        >
          <div className="space-y-3">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Card fields on Woo, no Tagada funnel. Download this plugin from the
              WooCommerce integration — not from this step.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                className="w-full gap-2"
                variant="outline"
                onClick={() => navigate(storeRoutes.INTEGRATION_CONFIG('woocommerce'))}
              >
                Open WooCommerce integration
              </Button>
              <a
                href={WOO_CHECKOUT_PLUGINS_DOCS}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-1.5 text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
              >
                Documentation
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </CollapsibleSection>

        {/* ─── Advanced ──────────────────────────────────────────── */}
        <SectionGroupHeader title="Advanced" />

        {/* Debug & Snippet */}
        <CollapsibleSection
          title="Developer Options"
          icon={Code2}
          defaultOpen={false}
        >
          <div className="space-y-3">
            {renderFeatureToggle(
              'Debug Mode',
              'Log TagadaPay API calls to browser console (dev only)',
              debug,
              setDebug,
            )}

            {snippetContent && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-500">Generated PHP Code</Label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCopySnippet}
                    className="h-6 gap-1 px-2 text-[10px]"
                  >
                    <Copy className="h-2.5 w-2.5" />
                    Copy
                  </Button>
                </div>
                <div className="max-h-[200px] overflow-auto rounded border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50">
                  <pre className="p-3 text-[10px] leading-relaxed text-slate-600 dark:text-slate-400">
                    {snippetContent.substring(0, 500)}
                    {snippetContent.length > 500 && '\n...'}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>
      </div>

      {/* ─── Footer ──────────────────────────────────────────────── */}
      <div className="border-t bg-slate-50 px-4 py-1.5 text-center dark:border-slate-700 dark:bg-slate-900">
        <p className="font-mono text-[9px] text-slate-400 dark:text-slate-600">Step ID: {step.id}</p>
      </div>
    </div>
  );
}
